import * as i1$1 from '@angular/common/http';
import { HttpErrorResponse, HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, PLATFORM_ID, NgModule } from '@angular/core';
import * as i1$3 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i1$2 from '@fleetmatics/ui.utilities';
import { FmCookieModule } from '@fleetmatics/ui.utilities';
import { from, EMPTY, BehaviorSubject, throwError, interval, Subject } from 'rxjs';
import { tap, switchMap, catchError, filter, map, take, finalize, delay, retry, repeat, takeUntil } from 'rxjs/operators';
import * as i1 from '@ngrx/store';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { jwtDecode } from 'jwt-decode';

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
const AUTH_CONFIG_TOKEN = new InjectionToken('AUTH_CONFIG_TOKEN');

/*
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */
const AUTH_SETTINGS_TOKEN = new InjectionToken('AUTH_SETTINGS_TOKEN');

/*
 * Copyright © 2018-2019. Verizon Connect Ireland Limited. All rights reserved.
 */
const SHOULD_REDIRECT_TO_LOGIN = new InjectionToken('SHOULD_REDIRECT_TO_LOGIN');
const AUTH_TOKEN_COOKIE_NAME = 'tkauth';
const AUTH_FLEETCARE_BACK_URL_COOKIE_NAME = 'fleetcarebackurl';
const ID_TOKEN_COOKIE_NAME = 'id_token';

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
var EUnauthorizedActions;
(function (EUnauthorizedActions) {
    EUnauthorizedActions["UnauthorizedRequest"] = "[Auth] Unauthorized Request";
    EUnauthorizedActions["ForbiddenRequest"] = "[Auth] Forbidden Request";
    EUnauthorizedActions["UnauthorizedNavigation"] = "[Auth] Unauthorized Navigation";
})(EUnauthorizedActions || (EUnauthorizedActions = {}));
class UnauthorizedRequest {
    constructor(payload) {
        this.payload = payload;
        this.type = EUnauthorizedActions.UnauthorizedRequest;
    }
}
class ForbiddenRequest {
    constructor(payload) {
        this.payload = payload;
        this.type = EUnauthorizedActions.ForbiddenRequest;
    }
}
class UnauthorizedNavigation {
    constructor() {
        this.type = EUnauthorizedActions.UnauthorizedNavigation;
    }
}

/*
 * Copyright © 2018-2025. Verizon Connect Ireland Limited. All rights reserved.
 */
class AuthService {
    static createReturnUrlQueryString(requestUrl) {
        const requestStrippedOfLanguage = requestUrl.replace(/^\/[a-z]{2}-[A-Z]{2}\//, '/');
        const encodedQueryString = encodeURIComponent(requestStrippedOfLanguage);
        return `?ReturnUrl=${encodedQueryString}`;
    }
    setAuthToken(token, expiry) { }
    getRefreshToken() {
        return null;
    }
    setRefreshToken(token) { }
    /**
     * Refreshes the access and refresh token if the refresh token exists
     */
    async refreshToken() { }
}

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
/**
 * Auth Guard.
 *
 * Use to restrict access to certain routes if the user is not authenticated.
 */
class AuthGuard {
    constructor(_store, _authService) {
        this._store = _store;
        this._authService = _authService;
    }
    /**
     * Called by Angular with an attempt to navigate to a route with this guard.
     * @returns True if the user is currently authenticated, false otherwise.
     */
    canActivate() {
        return from(this._authService.isAuthenticated()).pipe(tap(authenticated => {
            if (!authenticated) {
                this._store.dispatch(new UnauthorizedNavigation());
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthGuard, deps: [{ token: i1.Store }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthGuard }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: AuthService }]; } });

/*
 * Copyright © 2018-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
/**
 * Auth Interceptor.
 *
 * Used to intercept all HTTP requests and add the appropriate
 * authentication headers to each request.
 *
 * Module: {@link FmAuthModule}.
 */
class AuthInterceptor {
    constructor(store, injector, config) {
        this.store = store;
        this.injector = injector;
        this.config = config;
    }
    /**
     * Called by Angular every time an HTTP request is made.
     * @param req The current HttpRequest object.
     * @param next The next HttpHandler lined up to intercept the HttpRequest object.
     * @returns An Observable which will emit an HttpEvent when the request is resolved.
     */
    intercept(req, next) {
        if (this.isUrlWhitelisted(req.url)) {
            return next.handle(req);
        }
        return from(this.injector.get(AuthService).getAuthToken(this.config.auth.refreshIfExpired)).pipe(switchMap(authToken => {
            if (!authToken) {
                this.store.dispatch(new UnauthorizedRequest({
                    req,
                    err: new HttpErrorResponse({}),
                }));
                return EMPTY;
            }
            const headers = req.headers.set('Authorization', `Bearer ${authToken}`);
            return next.handle(req.clone({ headers }));
        }));
    }
    isUrlWhitelisted(url = '') {
        const whiteListedUrls = [this.config.auth.legacyLogoutEndpoint, this.config.auth.refreshEndpoint, ...(this.config.auth.whiteListedUrls || [])].filter(item => !!item);
        if (whiteListedUrls.includes(url)) {
            return true;
        }
        const regexWhitelist = this.config.auth.whiteListedUrlsRegex || [];
        return regexWhitelist.some((regex) => !!url.match(new RegExp(regex)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthInterceptor, deps: [{ token: i1.Store }, { token: i0.Injector }, { token: AUTH_CONFIG_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i0.Injector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG_TOKEN]
                }] }]; } });

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
/**
 * Http Status Code Enum.
 *
 * Represents the status code of an HTTP response, it contains the ones
 * being used internally by {@link FmAuthInterceptor}.
 */
var EHttpStatusCode;
(function (EHttpStatusCode) {
    EHttpStatusCode[EHttpStatusCode["Unauthorized"] = 401] = "Unauthorized";
    EHttpStatusCode[EHttpStatusCode["Forbbiden"] = 403] = "Forbbiden";
})(EHttpStatusCode || (EHttpStatusCode = {}));

class AuthHttpService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    logoutFromLegacy() {
        return this.http.post(this.config.auth.legacyLogoutEndpoint, undefined, {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        });
    }
    /**
     * This is NOT a OAuth token refresh, but rather a legacy keep-alive function that
     * simply keeps the session alive. If the token has expired, reveal itself
     *
     * @param legacy keepAlive endpoint
     */
    keepAlive(endpoint) {
        return this.http.post(endpoint, undefined, {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        });
    }
    refreshToken(refreshToken) {
        const params = new URLSearchParams();
        params.set('grant_type', 'refresh_token');
        params.set('client_id', this.config.auth.clientId ?? failWith$3('Missing Auth ClientId'));
        params.set('refresh_token', refreshToken);
        return this.http.post(this.config.auth.refreshEndpoint ?? failWith$3('Missing refresh endpoint'), params.toString(), {
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthHttpService, deps: [{ token: i1$1.HttpClient }, { token: AUTH_CONFIG_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthHttpService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthHttpService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG_TOKEN]
                }] }]; } });
function failWith$3(message) {
    throw new Error(message);
}

class AuthErrorInterceptor {
    constructor(store, settings, config, authHttpService, authService) {
        this.store = store;
        this.settings = settings;
        this.config = config;
        this.authHttpService = authHttpService;
        this.authService = authService;
        this.isRefreshing = false;
        this.accessTokenBehavior = new BehaviorSubject(null);
    }
    intercept(req, next) {
        return next.handle(req).pipe(catchError(err => {
            if (this.shouldRefresh(err)) {
                return this.refreshTokenAndRetry(req, next);
            }
            return throwError(err);
        }), tap({
            error: (err) => {
                if (req.url === this.config.auth.legacyLogoutEndpoint) {
                    return;
                }
                switch (err.status) {
                    case EHttpStatusCode.Unauthorized:
                        this.store.dispatch(new UnauthorizedRequest({ err, req }));
                        break;
                    case EHttpStatusCode.Forbbiden:
                        if (this.settings.appHandlesForbidden) {
                            return;
                        }
                        this.store.dispatch(new ForbiddenRequest({ err, req }));
                        break;
                    default:
                }
            },
        }));
    }
    shouldRefresh(err) {
        return Boolean(err.status === EHttpStatusCode.Unauthorized && this.config.auth.clientId && this.config.auth.refreshEndpoint && !!this.authService.getRefreshToken());
    }
    refreshTokenAndRetry(req, next) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.accessTokenBehavior.next(null);
            return this.authHttpService.refreshToken(this.authService.getRefreshToken() ?? failWith$2('Refresh token not found')).pipe(switchMap(tokens => {
                this.isRefreshing = false;
                this.accessTokenBehavior.next(tokens.access_token);
                this.authService.setAuthToken(tokens.access_token, tokens.expires_in);
                this.authService.setRefreshToken(tokens.refresh_token ?? failWith$2('No refresh token retrieved'));
                return next.handle(replaceBearerToken(req, tokens.access_token));
            }), catchError(err => {
                this.isRefreshing = false;
                this.store.dispatch(new UnauthorizedRequest({ err, req }));
                return throwError(err);
            }));
        }
        return this.accessTokenBehavior.pipe(filter(token => !!token), map(token => token), take(1), switchMap((token) => next.handle(replaceBearerToken(req, token))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthErrorInterceptor, deps: [{ token: i1.Store }, { token: AUTH_SETTINGS_TOKEN }, { token: AUTH_CONFIG_TOKEN }, { token: AuthHttpService }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthErrorInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthErrorInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_SETTINGS_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG_TOKEN]
                }] }, { type: AuthHttpService }, { type: AuthService }]; } });
function replaceBearerToken(req, token) {
    const newReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
    return newReq;
}
function failWith$2(message) {
    throw new Error(message);
}

/*
 * Copyright © 2018-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
const REFRESH_TOKEN_KEY = 'refresh_token';
/**
 * Auth Service.
 *
 * Provides functionality to manage authentication of the current user.
 *
 * Module: {@link FmAuthModule}
 */
class AuthBrowserService extends AuthService {
    constructor(cookieService, config, settings, authHttpService, platformId, document) {
        super();
        this.cookieService = cookieService;
        this.config = config;
        this.settings = settings;
        this.authHttpService = authHttpService;
        this.platformId = platformId;
        this.document = document;
    }
    /**
     * A boolean which will be true if the current user is authenticated, false otherwise.
     */
    async isAuthenticated() {
        const authToken = await this.getAuthToken();
        return !!authToken;
    }
    async getAuthToken(refreshIfExpired) {
        if (!refreshIfExpired) {
            return this.cookieService.getCookie(this.settings.authTokenCookieName ?? failWith$1('No auth cookie name supplied'));
        }
        const accessToken = await this.getAuthToken();
        if (!accessToken || !this.isValidJwt(accessToken)) {
            await this.refreshToken();
            return this.getAuthToken();
        }
        const expiry = jwtDecode(accessToken).exp;
        if (!expiry) {
            return accessToken;
        }
        const nowInMs = Date.now();
        const expiryInMs = expiry * 1000;
        if (expiryInMs < nowInMs) {
            await this.refreshToken();
            return this.getAuthToken();
        }
        return accessToken;
    }
    setAuthToken(token, expiry) {
        this.cookieService.setCookie(this.settings.authTokenCookieName ?? failWith$1('No auth cookie name supplied'), token, expiry / 60);
    }
    getRefreshToken() {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }
    setRefreshToken(token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }
    removeRefreshToken() {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    /**
     * Refreshes the access and refresh token if the refresh token exists
     */
    async refreshToken() {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            return;
        }
        // Using deprecated toPromise() for compatibility with rxjs 6 clients
        const tokenSet = await this.authHttpService.refreshToken(refreshToken).toPromise();
        if (tokenSet) {
            this.setAuthToken(tokenSet.access_token, tokenSet.expires_in);
            if (tokenSet.refresh_token) {
                this.setRefreshToken(tokenSet.refresh_token);
            }
        }
    }
    /**
     * Logout the current user from the legacy and the current app.
     */
    logout() {
        if (isPlatformBrowser(this.platformId)) {
            const refreshToken = this.getRefreshToken();
            this.authHttpService
                .logoutFromLegacy()
                .pipe(finalize(() => {
                this.cookieService.deleteCookie(this.settings.authTokenCookieName ?? failWith$1('No auth cookie name supplied'));
                const fleetcareBackUrlCookie = this.cookieService.getCookie(AUTH_FLEETCARE_BACK_URL_COOKIE_NAME);
                if (fleetcareBackUrlCookie && fleetcareBackUrlCookie.length > 0) {
                    // Fleetcare back url cookie is present, meaning user is impersonating. Redirect him to Fleetcare.
                    this.cookieService.deleteCookie(AUTH_FLEETCARE_BACK_URL_COOKIE_NAME);
                    this.redirectTo(fleetcareBackUrlCookie);
                }
                else {
                    // No Fleetcare back url cookie, do normal redirection to login page.
                    const pathName = top.location.pathname;
                    const search = top.location.search;
                    let redirectPath = this.config.auth.loginPageUrl + AuthService.createReturnUrlQueryString(pathName + search);
                    if (this.config.auth.albInternalPath) {
                        redirectPath = redirectPath.replace(`?ReturnUrl=/${this.config.auth.albInternalPath}`, '?ReturnUrl=');
                    }
                    if (refreshToken) {
                        const redirectUrl = `${this.getOrigin()}${encodeURIComponent(redirectPath)}`;
                        const auth0Logout = `${this.config.auth.logoutEndpoint}?client_id=${this.config.auth.clientId}&returnTo=${redirectUrl}`;
                        this.removeRefreshToken();
                        this.redirectTo(auth0Logout);
                    }
                    else {
                        this.redirectTo(redirectPath);
                    }
                }
            }))
                .subscribe();
        }
    }
    getTopMostWindow() {
        return this.document.defaultView?.top || this.document.defaultView;
    }
    getOrigin() {
        const win = this.getTopMostWindow();
        return win?.location.origin ?? '';
    }
    redirectTo(newLocation) {
        const win = this.getTopMostWindow();
        if (win) {
            win.location.href = newLocation;
        }
    }
    isValidJwt(token) {
        try {
            const decoded = jwtDecode(token);
            return !!decoded && typeof decoded === 'object' && 'exp' in decoded;
        }
        catch {
            return false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserService, deps: [{ token: i1$2.FmCookieService }, { token: AUTH_CONFIG_TOKEN }, { token: AUTH_SETTINGS_TOKEN }, { token: AuthHttpService }, { token: PLATFORM_ID }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.FmCookieService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_SETTINGS_TOKEN]
                }] }, { type: AuthHttpService }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });
function failWith$1(message) {
    throw new Error(message);
}

class AuthLoggerService {
    constructor() {
        this._logger = console;
    }
    logHttpError(message, request, error) {
        this._logger.warn({
            message,
            error,
            data: { req: request },
        });
    }
    logNavigationError(message) {
        this._logger.warn({ message });
    }
    warn(message) {
        this._logger.warn({ message });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthLoggerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthLoggerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthLoggerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
var EAuthActions;
(function (EAuthActions) {
    EAuthActions["Logout"] = "[Auth] Logout";
    EAuthActions["SetupKeepAlive"] = "[Auth] Setup Keep Alive";
})(EAuthActions || (EAuthActions = {}));
class Logout {
    constructor() {
        this.type = EAuthActions.Logout;
    }
}
class SetupKeepAlive {
    constructor(payload) {
        this.payload = payload;
        this.type = EAuthActions.SetupKeepAlive;
    }
}

/*
 * Copyright © 2018-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
// 10 min + 1 second
const repeatInterval = 601000;
// 1 min
const retryDelay = 60000;
const retryCount = 9;
class AuthEffects {
    constructor(_actions$, _authService, _authHttpService, _shouldRedirectToLogin, _platformId) {
        this._actions$ = _actions$;
        this._authService = _authService;
        this._authHttpService = _authHttpService;
        this._shouldRedirectToLogin = _shouldRedirectToLogin;
        this._platformId = _platformId;
        this.logout$ = createEffect(() => this._actions$.pipe(ofType(EAuthActions.Logout), tap(() => {
            this._authService.logout();
        })), { dispatch: false });
        this.setupKeepAlive$ = createEffect(() => this._actions$.pipe(ofType(EAuthActions.SetupKeepAlive), filter(() => isPlatformBrowser(this._platformId)), filter(() => !this._authService.getRefreshToken()), delay(repeatInterval), switchMap((action) => this._authHttpService.keepAlive(action.payload).pipe(tap((result) => {
            if (!result.d) {
                this._stopKeepAliveInterval.next();
            }
        }), filter((result) => !result.d && this._shouldRedirectToLogin), map(() => new Logout()), retry({ count: retryCount, delay: retryDelay }), repeat({ delay: () => interval(repeatInterval).pipe(takeUntil(this._stopKeepAliveInterval)) })))));
        this._stopKeepAliveInterval = new Subject();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthEffects, deps: [{ token: i1$3.Actions }, { token: AuthService }, { token: AuthHttpService }, { token: SHOULD_REDIRECT_TO_LOGIN }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthEffects }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: AuthService }, { type: AuthHttpService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SHOULD_REDIRECT_TO_LOGIN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
class UnauthorizedEffects {
    constructor(_actions$, _authLogger, _shouldRedirectToLogin) {
        this._actions$ = _actions$;
        this._authLogger = _authLogger;
        this._shouldRedirectToLogin = _shouldRedirectToLogin;
        this.logForbiddenRequest$ = createEffect(() => this._actions$.pipe(ofType(EUnauthorizedActions.ForbiddenRequest), tap((action) => {
            this._authLogger.logHttpError('Forbidden API request', action.payload.req, action.payload.err);
        })), { dispatch: false });
        this.logUnauthorizedRequest$ = createEffect(() => this._actions$.pipe(ofType(EUnauthorizedActions.UnauthorizedRequest), tap((action) => {
            this._authLogger.logHttpError('Unauthorized API request', action.payload.req, action.payload.err);
        })), { dispatch: false });
        this.logUnauthorizedNavigation$ = createEffect(() => this._actions$.pipe(ofType(EUnauthorizedActions.UnauthorizedNavigation), tap(() => this._authLogger.logNavigationError('Unauthorized Navigation'))), { dispatch: false });
        this.logoutOnError$ = createEffect(() => this._actions$.pipe(ofType(EUnauthorizedActions.UnauthorizedNavigation, EUnauthorizedActions.UnauthorizedRequest, EUnauthorizedActions.ForbiddenRequest), tap(() => this._authLogger.warn('Redirecting to Login')), filter(() => this._shouldRedirectToLogin), map(() => new Logout())));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UnauthorizedEffects, deps: [{ token: i1$3.Actions }, { token: AuthLoggerService }, { token: SHOULD_REDIRECT_TO_LOGIN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UnauthorizedEffects }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UnauthorizedEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: AuthLoggerService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SHOULD_REDIRECT_TO_LOGIN]
                }] }]; } });

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
/**
 * Auth Browser Module.
 *
 * Provides {@link FmAuthInterceptor} and {@link FmAuthService}.
 */
class AuthBrowserModule {
    static forRoot(configs, settings = { appHandlesForbidden: false }, shouldRedirectToLogin = true) {
        return {
            ngModule: AuthBrowserModule,
            providers: [
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: AuthInterceptor,
                    multi: true,
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: AuthErrorInterceptor,
                    multi: true,
                },
                { provide: AUTH_CONFIG_TOKEN, useValue: configs },
                {
                    provide: AUTH_SETTINGS_TOKEN,
                    useValue: {
                        appHandlesForbidden: settings.appHandlesForbidden || false,
                        authTokenCookieName: settings.authTokenCookieName || AUTH_TOKEN_COOKIE_NAME,
                    },
                },
                { provide: SHOULD_REDIRECT_TO_LOGIN, useValue: shouldRedirectToLogin },
                { provide: AuthService, useClass: AuthBrowserService },
                AuthHttpService,
                AuthLoggerService,
                AuthGuard,
            ],
        };
    }
    constructor(store, config) {
        this.store = store;
        this.config = config;
        if (config.auth.shouldKeepSessionAlive) {
            store.dispatch(new SetupKeepAlive(config.auth.keepSessionAliveEndpoint ?? failWith('Missing keep alive session endpoint')));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserModule, deps: [{ token: i1.Store }, { token: AUTH_CONFIG_TOKEN }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserModule, imports: [HttpClientModule, i1$3.EffectsFeatureModule, FmCookieModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserModule, imports: [HttpClientModule, EffectsModule.forFeature([AuthEffects, UnauthorizedEffects]), FmCookieModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthBrowserModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule, EffectsModule.forFeature([AuthEffects, UnauthorizedEffects]), FmCookieModule],
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG_TOKEN]
                }] }]; } });
function failWith(message) {
    throw new Error(message);
}

/*
 * Copyright © 2018-2023. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2019-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
/*
 * Public API Surface of authentication-lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG_TOKEN, AUTH_FLEETCARE_BACK_URL_COOKIE_NAME, AUTH_SETTINGS_TOKEN, AUTH_TOKEN_COOKIE_NAME, AuthBrowserModule, AuthBrowserService, AuthEffects, AuthErrorInterceptor, AuthGuard, AuthHttpService, AuthInterceptor, AuthLoggerService, AuthService, EAuthActions, EHttpStatusCode, EUnauthorizedActions, ForbiddenRequest, ID_TOKEN_COOKIE_NAME, Logout, SHOULD_REDIRECT_TO_LOGIN, SetupKeepAlive, UnauthorizedEffects, UnauthorizedNavigation, UnauthorizedRequest };
